import { useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import type { DwellPlace, DwellReason } from "@backstory/protocol";
import { useAuth } from "../../../auth/store";
import { EmptySearchMark, ErrorState } from "../../../components/States";
import { PageHeader } from "../../../components/ui";
import { insightsApi, insightsKeys } from "../api";
import { useSlowPending, WakingNumbers } from "../WakingNumbers";
import { RangePicker, useRange } from "../components";
import { rangeFromSearch, rangeToSearch } from "../range";

const REASONS: Record<DwellReason, { title: string; detail: string }> = {
  stuck: { title: "People get stuck", detail: "Rage clicks, dead clicks, or a field they struggle with." },
  slow: { title: "The page is slow", detail: "The page or a request on it runs slowly." },
  form: { title: "A form holds them", detail: "They start a form and leave it unfinished." },
  circling: { title: "They go back and forth", detail: "They leave this page and come back." },
  reading: { title: "They read down the page", detail: "They scroll well down it, and little else goes wrong." },
  quiet: { title: "No clear problem", detail: "They stay, and nothing in the recording stands out." },
};

/** How long a visit stayed, in words a person can scan. */
export function stayLabel(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const total = Math.round(ms / 1000);
  if (total < 60) return total === 1 ? "1 sec" : `${total} sec`;
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes < 60) {
    if (seconds === 0) return minutes === 1 ? "1 min" : `${minutes} min`;
    return `${minutes} min ${seconds} sec`;
  }
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (rem === 0) return hours === 1 ? "1 hr" : `${hours} hr`;
  return `${hours} hr ${rem} min`;
}

/** The likely reason, plus how many visits showed it when that count exists. */
export function dwellReasonText(place: Pick<DwellPlace, "reason" | "reasonSessions" | "sessions">): { title: string; detail: string } {
  const known = REASONS[place.reason] ?? REASONS.quiet;
  if (!place.reasonSessions) return known;
  const visits = place.sessions === 1 ? "session" : "sessions";
  return { title: known.title, detail: `${known.detail} On ${place.reasonSessions.toLocaleString()} of ${place.sessions.toLocaleString()} ${visits}.` };
}

export function TimeSpentPage() {
  const auth = useAuth();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const { range } = useRange();
  const q = useQuery({
    queryKey: insightsKeys.dwell(range, auth.currentProjectId),
    queryFn: () => insightsApi.dwell(range),
    retry: 0,
  });
  const places = q.data?.places ?? [];
  const period = rangeToSearch(rangeFromSearch(search));
  const slowLoad = useSlowPending(q.isPending);

  return (
    <>
      <PageHeader
        title="Time spent"
        subtitle="The pages people spend the most time on, and why."
        actions={<RangePicker />}
      />
      {q.isError ? (
        <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>
      ) : q.isPending && slowLoad ? (
        <WakingNumbers />
      ) : !q.isPending && places.length === 0 ? (
        <div className="card empty dwell-empty" role="status">
          <EmptySearchMark />
          <h3>No pages in this period</h3>
          <p>Time on a page shows up after a few sessions.</p>
        </div>
      ) : (
        <div className="card dwell-card">
          <table className="table dwell-table" aria-label="Where people stay" aria-busy={q.isPending || undefined}>
            <thead>
              <tr>
                <th>Place</th>
                <th>Typical time</th>
                <th>A long session</th>
                <th>Sessions</th>
                <th>Likely reason</th>
                <th><span className="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {q.isPending && Array.from({ length: 4 }).map((_, i) => (
                <tr key={`sk-${i}`} className="static" aria-hidden="true">
                  {Array.from({ length: 6 }).map((__, j) => (<td key={j}><span className="skeleton">loading</span></td>))}
                </tr>
              ))}
              {places.map((p) => {
                const reason = dwellReasonText(p);
                return (
                  <tr key={p.route}>
                    <td data-label="Place"><span className="dwell-place">{p.route}</span></td>
                    <td className="mono" data-label="Typical time">{stayLabel(p.medianMs)}</td>
                    <td className="mono" data-label="A long session">{stayLabel(p.p90Ms)}</td>
                    <td className="mono" data-label="Sessions">{p.sessions.toLocaleString()}</td>
                    <td data-label="Likely reason">
                      <span className="ins-title">{reason.title}</span>
                      <span className="ins-sub">{reason.detail}</span>
                    </td>
                    <td className="dwell-links">
                      <Link to="/heatmaps" search={{ ...period, route: p.route } as never}>Heatmap</Link>
                      {p.sampleSessionId && <Link to="/sessions/$id" params={{ id: p.sampleSessionId }}>Watch a long session</Link>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
